import { useState, useEffect } from "react";
import { Info, X, CheckCircle2, Circle } from "lucide-react";

export type RequiredItem = string | { label: string; met?: boolean };

interface Props {
  /** Stable id used for localStorage dismissal key */
  stepId: string;
  /** Bullet items shown under the heading. May be plain strings (static) or objects with a `met` flag (dynamic). */
  required?: RequiredItem[];
  /** Optional plain-text body shown above bullets. */
  body?: string;
  /** Tip / footer line shown below bullets. */
  tip?: string;
  /** Heading override. Defaults to "Required to complete this step:" if `required` items provided, otherwise "About this step". */
  heading?: string;
}

const STORAGE_PREFIX = "wizardStepHelpDismissed:";

function normalize(items?: RequiredItem[]): { label: string; met?: boolean }[] {
  return (items ?? []).map((i) => (typeof i === "string" ? { label: i } : i));
}

export function StepHelpBanner({ stepId, required, body, tip, heading }: Props) {
  const storageKey = `${STORAGE_PREFIX}${stepId}`;
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      setDismissed(localStorage.getItem(storageKey) === "1");
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  const items = normalize(required);
  // Items that participate in dynamic tracking (have an explicit met flag)
  const trackable = items.filter((i) => typeof i.met === "boolean");
  const hasTrackable = trackable.length > 0;
  const allMet = hasTrackable && trackable.every((i) => i.met);

  // Auto-hide once every tracked requirement is satisfied.
  if (dismissed || allMet) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(storageKey, "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const hasRequired = items.length > 0;
  const resolvedHeading =
    heading ??
    (hasRequired
      ? hasTrackable
        ? "Complete these to finish this step:"
        : "Required to complete this step:"
      : "About this step");

  const completedCount = trackable.filter((i) => i.met).length;

  return (
    <div className="relative rounded-lg border border-border bg-muted/40 p-4 mb-4 text-sm">
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss help"
        className="absolute top-2 right-2 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="flex gap-3 pr-6">
        <Info className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
        <div className="space-y-2 w-full">
          {body && <p className="text-muted-foreground leading-relaxed">{body}</p>}
          {hasRequired && (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-foreground">{resolvedHeading}</p>
                {hasTrackable && (
                  <span className="text-xs text-muted-foreground shrink-0">
                    {completedCount}/{trackable.length} done
                  </span>
                )}
              </div>
              <ul className="space-y-1">
                {items.map((item, i) => {
                  const tracked = typeof item.met === "boolean";
                  return (
                    <li
                      key={i}
                      className={
                        "flex items-start gap-2 " +
                        (item.met
                          ? "text-muted-foreground line-through"
                          : "text-muted-foreground")
                      }
                    >
                      {tracked ? (
                        item.met ? (
                          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
                        ) : (
                          <Circle className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground/60" />
                        )
                      ) : (
                        <span className="mt-1.5 ml-1 mr-1 w-1 h-1 rounded-full bg-muted-foreground shrink-0" />
                      )}
                      <span>{item.label}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          {!hasRequired && !body && (
            <p className="font-medium text-foreground">{resolvedHeading}</p>
          )}
          {tip && <p className="text-muted-foreground italic">{tip}</p>}
        </div>
      </div>
    </div>
  );
}
